import {
  GET_NEWEST_REVIEWS_BY_JOB_TYPE,
  GET_PRO_COUNT,
  GET_POPULAR_PRICING_ITEMS,
  LIST_BLOG_POSTS,
  LIST_BLOG_POSTS_BY_CATEGORY,
  LIST_PRO_BLOG_POSTS_BY_JOB_TYPE,
  LIST_PROS,
  LIST_PROS_GROUP,
  LIST_SAMPLE_REQUESTS,
  EXPAND_PRICING_ITEMS,
  GET_CASE_STUDIES,
  GET_POPULAR_REQUESTS,
  ADD_FILTER,
  REMOVE_FILTER,
  INITIALIZE_FILTERS,
  REMOVE_MATCHING_CRITERIA_FILTER,
  ADD_MATCHING_CRITERIA_FILTER,
  UPDATE_TIME_FILTER,
  UPDATE_DAY_FILTER,
  GET_PRO_CATEGORIES_FOR_SIGN_UP,
} from './types';
import type { ListProsParameters, UpdateFiltersParams } from '../marketing-page.types';
import type { FilterState } from './types';
import type { Dispatch } from 'react';
import type { RootState } from 'store/types';
import { transformFilterStateToListProParameters } from '../utils';

export function getNewestReviewsByJobType(jobTypeId: string) {
  return {
    payload: {
      jobTypeId,
    },
    type: GET_NEWEST_REVIEWS_BY_JOB_TYPE,
  };
}

export function getProJobCategoriesForSignUp() {
  return {
    type: GET_PRO_CATEGORIES_FOR_SIGN_UP,
  };
}

export function getProCount({
  cityKey,
  jobTypeId,
  language,
  prefectureKey,
}: {
  cityKey?: string;
  jobTypeId: string;
  language?: string;
  prefectureKey?: string;
}) {
  return {
    payload: {
      cityKey,
      jobTypeId,
      language,
      prefectureKey,
    },
    type: GET_PRO_COUNT,
  };
}

export function listBlogPosts() {
  return {
    type: LIST_BLOG_POSTS,
  };
}

export function listBlogPostsByCategory(id: string) {
  return {
    payload: {
      id,
    },
    type: LIST_BLOG_POSTS_BY_CATEGORY,
  };
}

export function listProBlogPostsByJobType(jobTypeId: string) {
  return {
    payload: {
      jobTypeId,
    },
    type: LIST_PRO_BLOG_POSTS_BY_JOB_TYPE,
  };
}

export function listPros({
  cityGroup,
  cityKey,
  day,
  jobTypeId,
  language = 'ja',
  matchingCriteria,
  page,
  prefectureKey,
  priceRange,
  pricingMenuItemKey,
  size,
  skip,
  time,
}: ListProsParameters) {
  return {
    payload: {
      cityGroup,
      cityKey,
      day,
      jobTypeId,
      language,
      matchingCriteria,
      page,
      prefectureKey,
      priceRange,
      pricingMenuItemKey,
      size,
      skip,
      time,
    },
    type: LIST_PROS,
  };
}

export function listProsGroup({
  cityGroup,
  cityKey,
  day,
  excludeCities,
  jobTypeId,
  language = 'ja',
  matchingCriteria,
  page,
  prefectureKey,
  priceRange,
  pricingMenuItemKey,
  size,
  skip,
  time,
}: {
  cityGroup?: string;
  cityKey?: string;
  day?: string;
  excludeCities?: Array<string>;
  jobTypeId: string;
  language?: string;
  matchingCriteria?: string;
  page: number;
  prefectureKey?: string;
  priceRange?: string;
  pricingMenuItemKey?: string;
  size: number;
  skip?: number;
  time?: string;
}) {
  return {
    payload: {
      cityGroup,
      cityKey,
      day,
      excludeCities,
      jobTypeId,
      language,
      matchingCriteria,
      page,
      prefectureKey,
      priceRange,
      pricingMenuItemKey,
      size,
      skip,
      time,
    },
    type: LIST_PROS_GROUP,
  };
}

export function getCaseStudies({
  currentPage,
  cityKey,
  prefectureKey,
  jobTypeId,
}: {
  cityKey?: string;
  currentPage: number;
  jobTypeId: string;
  prefectureKey?: string;
}) {
  return {
    payload: {
      currentPage,
      cityKey,
      prefectureKey,
      jobTypeId,
    },
    type: GET_CASE_STUDIES,
  };
}

export function getPopularRequests({
  cityKey,
  prefectureKey,
  jobTypeId,
}: {
  cityKey?: string;
  jobTypeId: string;
  prefectureKey?: string;
}) {
  return {
    payload: {
      cityKey,
      prefectureKey,
      jobTypeId,
    },
    type: GET_POPULAR_REQUESTS,
  };
}

export function listSampleRequests({
  cityId,
  jobTypeId,
  language = 'ja',
  prefectureId,
  limit,
}: {
  cityId?: string;
  jobTypeId: string;
  language?: string;
  limit?: number;
  prefectureId?: string;
}) {
  return {
    payload: {
      cityId,
      jobTypeId,
      language,
      limit,
      prefectureId,
    },
    type: LIST_SAMPLE_REQUESTS,
  };
}

export function expandPopularPricingItems() {
  return { type: EXPAND_PRICING_ITEMS };
}

export function getPopularPricingItems({
  cityKey,
  jobTypeId,
  language = 'ja',
  prefectureKey,
}: {
  cityKey?: string;
  jobTypeId: string;
  language?: string;
  prefectureKey?: string;
}) {
  return {
    payload: { cityKey, jobTypeId, language, prefectureKey },
    type: GET_POPULAR_PRICING_ITEMS,
  };
}

type AddFilterPayload = { filterId: string; filterValue: Array<string> };
export function addFilter({ filterId, filterValue }: AddFilterPayload) {
  return { filterId, filterValue, type: ADD_FILTER };
}

type RemoveFilterPayload = { filterId: Omit<keyof FilterState, 'matchingCriteria'> };
export function removeFilter({ filterId }: RemoveFilterPayload) {
  return { filterId, type: REMOVE_FILTER };
}

export function initializeFilters(filters: FilterState) {
  return { filters, type: INITIALIZE_FILTERS };
}

function updateDayFilter({ day }: { day: string }) {
  return { day, type: UPDATE_DAY_FILTER };
}

function updateTimeFilter({ time }: { time: string }) {
  return { time, type: UPDATE_TIME_FILTER };
}

function removeMatchingCriteriaFilter({
  matchingCriteriaKey,
  matchingCriteriaValues,
}: {
  matchingCriteriaKey: string;
  matchingCriteriaValues: Array<string> | null;
}) {
  return { matchingCriteriaKey, matchingCriteriaValues, type: REMOVE_MATCHING_CRITERIA_FILTER };
}

function addMatchingCriteriaFilter({
  matchingCriteriaKey,
  matchingCriteriaValues,
}: {
  matchingCriteriaKey: string;
  matchingCriteriaValues: Array<string>;
}) {
  return { matchingCriteriaKey, matchingCriteriaValues, type: ADD_MATCHING_CRITERIA_FILTER };
}

export function updateFiltersAndListNewPros(
  { filterType, filterId, filterValue }: UpdateFiltersParams,
  listProParameters: ListProsParameters
) {
  return function updateFiltersThunk(dispatch: Dispatch<any>, getState: () => RootState) {
    const { filters: currentFilters } = getState().marketingPages;

    const isRemovalOperation = filterValue.length === 0;
    switch (filterType) {
      case 'priceRange': {
        if (isRemovalOperation) {
          dispatch(removeFilter({ filterId }));
        } else {
          dispatch(addFilter({ filterId, filterValue }));
        }
        break;
      }
      case 'DateTime': {
        if (!Array.isArray(filterValue)) {
          return;
        }
        const [day, time] = filterValue;
        if (day) {
          dispatch(updateDayFilter({ day }));
        } else {
          dispatch(removeFilter({ filterId: 'day' }));
        }
        if (time) {
          dispatch(updateTimeFilter({ time }));
        } else {
          dispatch(removeFilter({ filterId: 'time' }));
        }
        break;
      }
      case 'matchingCriteria': {
        const [matchingCriteriaKey, matchingCriteriaValue] = filterId.split('.');
        const matchingCriteriaValuesForKey = currentFilters.matchingCriteria[matchingCriteriaKey];
        let matchingCriteriaValues;
        if (isRemovalOperation && Array.isArray(matchingCriteriaValuesForKey)) {
          matchingCriteriaValues = matchingCriteriaValuesForKey.filter(
            (value) => value !== matchingCriteriaValue
          );
          dispatch(
            removeMatchingCriteriaFilter({
              matchingCriteriaKey,
              matchingCriteriaValues:
                matchingCriteriaValues.length === 0 ? null : matchingCriteriaValues,
            })
          );
        } else {
          if (matchingCriteriaValuesForKey) {
            matchingCriteriaValues = [...matchingCriteriaValuesForKey, matchingCriteriaValue];
          } else {
            matchingCriteriaValues = [matchingCriteriaValue];
          }
          dispatch(addMatchingCriteriaFilter({ matchingCriteriaKey, matchingCriteriaValues }));
        }
        break;
      }
      case 'removeAll': {
        dispatch(removeFilter({ filterId: 'priceRange' }));
        dispatch(removeFilter({ filterId: 'day' }));
        dispatch(removeFilter({ filterId: 'time' }));

        const matchingCriteriaKeys = Object.keys(currentFilters.matchingCriteria);

        matchingCriteriaKeys.forEach((matchingCriteriaKey) => {
          dispatch(
            removeMatchingCriteriaFilter({
              matchingCriteriaKey,
              matchingCriteriaValues: null,
            })
          );
        });
      }
    }
    const newFilters = getState().marketingPages.filters;

    const filterParams = transformFilterStateToListProParameters(newFilters);

    dispatch(listPros({ ...listProParameters, ...filterParams }));

    if (listProParameters.cityGroup) {
      dispatch(listProsGroup({ ...listProParameters, ...filterParams }));
    }
  };
}
